// File: src/components/pages/payments/PaymentOptionsInline.jsx
// Inline payment collection form for tab content area

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Calculator, CreditCard, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { studentAPI, instructorConfigAPI, paymentAPI } from '../../../services/api';
import './PaymentOptionsInline.css';

const PaymentOptionsInline = ({ onBack, onPaymentCreated }) => {
  const { getAccessTokenSilently } = useAuth0();
  
  // Form state
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [instructorConfig, setInstructorConfig] = useState(null);
  
  // Payment fields
  const [paymentOption, setPaymentOption] = useState('Introduction');
  const [selectedHours, setSelectedHours] = useState(3);
  const [selectedDays, setSelectedDays] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [manualAmount, setManualAmount] = useState(1);
  const [examAmount, setExamAmount] = useState(100);
  const [manualClasses, setManualClasses] = useState(1);
  const [notes, setNotes] = useState('');
  const [lessonDuration, setLessonDuration] = useState(2); // hours, overridden by config on load
  
  // Calculated values
  const [calculation, setCalculation] = useState({
    hourlyRate: 0,
    originalAmount: 0,
    discountType: null,
    discountAmount: 0,
    discountPercentage: 0,
    finalAmount: 0,
    numberOfClasses: 0,
    lessonDuration: 0 
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const paymentOptions = [
    { id: 'Introduction', label: 'Introduction', icon: '🎓', description: 'Perfect for getting started' },
    { id: '3ClassesPlus', label: '3+ Classes', icon: '⏰', description: 'Extended learning package' },
    { id: 'Daily', label: 'Daily', icon: '📅', description: 'Full day discounted rate' },
    { id: 'Exam', label: 'Exam', icon: '✍️', description: 'Test day preparation' },
    { id: 'Other', label: 'Other', icon: '🎯', description: 'Custom package' }
  ];

  // Load students and instructor config on mount
  useEffect(() => {
    loadData();
  }, []);

  // Recalculate when dependencies change
  useEffect(() => {
    if (selectedStudent && instructorConfig) {
      calculatePayment();
    }
  }, [selectedStudent, paymentOption, selectedHours, selectedDays, manualAmount, manualClasses, instructorConfig, lessonDuration, examAmount]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const response = await studentAPI.getStudentsByStatus('active', getAccessTokenSilently);
      
      let activeStudents = [];
      if (Array.isArray(response)) {
        activeStudents = response;
      } else if (response && Array.isArray(response.students)) {
        activeStudents = response.students;
      } else if (response && response.data && Array.isArray(response.data)) {
        activeStudents = response.data;
      }
      
      setStudents(activeStudents);
      
      const config = await instructorConfigAPI.getConfiguration(getAccessTokenSilently);
      
      if (!config || !config.data) {
        setError('No instructor configuration found. Please configure your settings first.');
        setLoading(false);
        return;
      }

      setInstructorConfig(config.data);
      setLessonDuration(config.data.lessonDuration || 2);
      setLoading(false);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load students and configuration');
      setLoading(false);
    }
  };

  const calculatePayment = () => {
    if (!selectedStudent || !instructorConfig) {
      return;
    }

    let hourlyRate = 0;
    let originalAmount = 0;
    let discountType = null;
    let discountAmount = 0;
    let discountPercentage = 0;
    let finalAmount = 0;
    let numberOfClasses = 0;
    let computedLessonDuration = 0; 

    // Determine hourly rate based on student's booking type preference
    if (selectedStudent.vehicleOption === 'Automatic') {
      hourlyRate = instructorConfig.hourlyRateAutomatic || 0;
    } else {
      hourlyRate = instructorConfig.hourlyRateManual || 0;
    }

    switch (paymentOption) {
      case 'Introduction':
        numberOfClasses = 5;
        computedLessonDuration = lessonDuration; // from state
        originalAmount = hourlyRate * computedLessonDuration * numberOfClasses;
        discountPercentage = instructorConfig.discountIntroductory || 0;
        discountType = 'Introductory Discount';
        discountAmount = originalAmount * (discountPercentage / 100);
        finalAmount = originalAmount - discountAmount;
        break;

      case '3ClassesPlus':
        computedLessonDuration = lessonDuration; // from state
        numberOfClasses = selectedHours; // now = number of classes
        originalAmount = hourlyRate * computedLessonDuration * numberOfClasses;
        discountType = '3+ Classes Discount';
        const classDiscount = instructorConfig.discount6HoursPlus || 0;
        discountAmount = numberOfClasses * computedLessonDuration * classDiscount;
        discountPercentage = originalAmount > 0 ? (discountAmount / originalAmount) * 100 : 0;
        finalAmount = originalAmount - discountAmount;
        break;

      case 'Daily':
        computedLessonDuration = lessonDuration; // from state
        numberOfClasses = selectedDays;
        originalAmount = hourlyRate * computedLessonDuration * numberOfClasses;
        const multiDayDiscount = instructorConfig.discountMultiDay || 0;
        discountPercentage = multiDayDiscount;
        discountType = 'Multi-Day Discount';
        discountAmount = originalAmount * (multiDayDiscount / 100);
        finalAmount = originalAmount - discountAmount;
        break;

      case 'Exam':
        numberOfClasses = 1;
        originalAmount = parseFloat(examAmount) || 100;
        discountType = null;
        discountAmount = 0;
        finalAmount = parseFloat(examAmount) || 100;
        break;

      case 'Other':
        numberOfClasses = parseInt(manualClasses) || 1;
        computedLessonDuration = lessonDuration; // from state — instructor-selected
        originalAmount = parseFloat(manualAmount) || 1;
        discountType = null;
        discountAmount = 0;
        finalAmount = originalAmount;
        break;

      default:
        break;
    }

    setCalculation({
      hourlyRate,
      originalAmount,
      discountType,
      discountAmount,
      discountPercentage,
      finalAmount,
      numberOfClasses,
      lessonDuration: computedLessonDuration
    });
  };

  const handleStudentChange = (studentId) => {
    const student = students.find(s => s.studentId === studentId);
    setSelectedStudent(student);
  };

  const handlePaymentOptionChange = (option) => {
    setPaymentOption(option);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedStudent) {
      setError('Please select a student');
      return;
    }

    if (paymentOption === 'Other' && (!manualAmount || !manualClasses)) {
      setError('Please enter payment amount and number of classes');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const paymentData = {
        studentId: selectedStudent.studentId,
        instructorId: selectedStudent.providerId,
        studentName: `${selectedStudent.firstName} ${selectedStudent.lastName}`,
        paymentDate: new Date().toISOString().split('T')[0],
        paymentAmount: calculation.finalAmount,
        originalAmount: calculation.originalAmount,
        paymentMethod,
        paymentOption,
        discountType: calculation.discountType,
        discountAmount: calculation.discountAmount,
        numberOfClasses: calculation.numberOfClasses,
        hourlyRate: calculation.hourlyRate,
        notes: notes.trim() || null,
        lessonDuration: calculation.lessonDuration
      };

      await paymentAPI.createPayment(paymentData, getAccessTokenSilently);

      setSuccess(true);
      setTimeout(() => {
        onPaymentCreated();
      }, 2000);
    } catch (err) {
      console.error('Error creating payment:', err);
      setError('Failed to create payment record');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="inline-payment-container">
        <div className="inline-success-state">
          <CheckCircle size={64} className="success-icon" />
          <h2>Payment Recorded Successfully!</h2>
          <p>The payment has been added to the student's record</p>
          <button onClick={onBack} className="btn-back-to-list">
            Back to Payments
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="inline-payment-container">
      {/* Page Header */}
      <div className="inline-page-header">
        <button onClick={onBack} className="btn-back">
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <div className="page-header-content">
          <h1>Payment Options</h1>
          <p>Select your preferred plan and see detailed breakdown</p>
        </div>
      </div>

      {error && (
        <div className="inline-error-banner">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      <div className="inline-payment-grid">
        {/* Left Column - Options Selection */}
        <div className="inline-options-column">
          <div className="inline-card">
            <div className="inline-card-header">
              <h3>Select Package</h3>
            </div>
            <div className="inline-card-body">
              {/* Student Selection */}
              <div className="inline-form-field">
                <label>Choose Student</label>
                <select
                  value={selectedStudent?.studentId || ''}
                  onChange={(e) => handleStudentChange(e.target.value)}
                  disabled={!students || students.length === 0}
                >
                  <option value="">
                    {students && students.length === 0 
                      ? '-- No Active Students --' 
                      : '-- Select Student --'}
                  </option>
                  {students.map(student => (
                    <option key={student.studentId} value={student.studentId}>
                      {student.firstName} {student.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Payment Options Grid */}
              <div className="inline-form-field">
                <label>Payment Package</label>
                <div className="inline-options-grid">
                  {paymentOptions.map(option => (
                    <button
                      key={option.id}
                      type="button"
                      className={`inline-option-card ${paymentOption === option.id ? 'selected' : ''}`}
                      onClick={() => handlePaymentOptionChange(option.id)}
                      disabled={!selectedStudent}
                    >
                      <div className="option-radio">
                        {paymentOption === option.id && <div className="radio-dot" />}
                      </div>
                      <span className="option-icon">{option.icon}</span>
                      <span className="option-label">{option.label}</span>
                      <span className="option-description">{option.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Conditional Inputs */}
              {paymentOption === '3ClassesPlus' && selectedStudent && (
                <div className="inline-form-field">
                  <label>Select Classes</label>
                  <select
                    value={selectedHours}
                    onChange={(e) => setSelectedHours(parseInt(e.target.value))}
                  >
                    <option value={3}>3 Classes</option>
                    <option value={4}>4 Classes</option>
                    <option value={5}>5 Classes</option>
                    <option value={6}>6 Classes</option>
                  </select>
                </div>
              )}

              {paymentOption === 'Daily' && selectedStudent && (
                <div className="inline-form-field">
                  <label>Number of Days</label>
                  <select
                    value={selectedDays}
                    onChange={(e) => setSelectedDays(parseInt(e.target.value))}
                  >
                    <option value={1} selected="true">1 Lesson </option>
                    <option value={2}>2 Lessons </option>
                  </select>
                </div>
              )}


              {/* Exam — editable amount */}
              {paymentOption === 'Exam' && selectedStudent && (
                <div className="inline-form-field">
                  <label>Exam Amount (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={examAmount}
                    onChange={(e) => setExamAmount(e.target.value)}
                    placeholder="100.00"
                  />
                </div>
              )}

              {/* Lesson Duration — Introduction, 3ClassesPlus, Daily */}
              {['Introduction', '3ClassesPlus', 'Daily'].includes(paymentOption) && selectedStudent && (
                <div className="inline-form-field">
                  <label>Lesson Duration</label>
                  <select
                    value={lessonDuration}
                    onChange={(e) => setLessonDuration(parseFloat(e.target.value))}
                  >
                    {[1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3, 3.25, 3.5, 3.75, 4].map(d => {
                      const hrs = Math.floor(d);
                      const mins = Math.round((d - hrs) * 60);
                      const label = mins === 0 ? `${hrs}h` : `${hrs}h ${mins}m`;
                      const isDefault = d === (instructorConfig?.lessonDuration || 2);
                      return (
                        <option key={d} value={d}>
                          {label}{isDefault ? ' (default)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
              {paymentOption === 'Other' && selectedStudent && (
                <div className="inline-form-field-group">
                  <div className="inline-form-field">
                    <label>Payment Amount (£)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={manualAmount}
                      onChange={(e) => setManualAmount(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="inline-form-field">
                    <label>Number of Classes</label>
                    <input
                      type="number"
                      min="1"
                      value={manualClasses}
                      onChange={(e) => setManualClasses(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div className="inline-form-field">
                    <label>Lesson Duration</label>
                    <select
                      value={lessonDuration}
                      onChange={(e) => setLessonDuration(parseFloat(e.target.value))}
                    >
                      {[1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3, 3.25, 3.5, 3.75, 4].map(d => {
                        const hrs  = Math.floor(d);
                        const mins = Math.round((d - hrs) * 60);
                        const label = mins === 0 ? `${hrs}h` : `${hrs}h ${mins}m`;
                        const isDefault = d === (instructorConfig?.lessonDuration || 2);
                        return (
                          <option key={d} value={d}>
                            {label}{isDefault ? ' (default)' : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              )}

              {/* Payment Method */}
              {selectedStudent && (
                <div className="inline-form-field">
                  <label>Payment Method</label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <option value="Cash">Cash</option>
                    {/*<<option value="Card">Card</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Cheque">Cheque</option>*/}
                  </select>
                </div>
              )}

              {/* Notes */}
              {selectedStudent && (
                <div className="inline-form-field">
                  <label>Additional Notes (Optional)</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows="3"
                    placeholder="Add any notes about this payment..."
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Breakdown */}
        <div className="inline-breakdown-column">
          {selectedStudent && calculation.finalAmount > 0 ? (
            <>
              <div className="inline-card">
                <div className="inline-card-header gradient">
                  <Calculator size={20} />
                  <h3>Payment Breakdown</h3>
                </div>
                <div className="inline-card-body">
                  <div className="breakdown-items">
                    {/* Hourly Rate */}
                    <div className="breakdown-item">
                      <span className="breakdown-label">Hourly Rate</span>
                      <span className="breakdown-value">£{calculation.hourlyRate.toFixed(2)}</span>
                    </div>

                    {/* Number of Classes/Hours */}
                    {paymentOption === 'Daily' ? (
                      <div className="breakdown-item highlight">
                        <span className="breakdown-label">Daily Rate</span>
                        <span className="breakdown-calculation">
                          £{calculation.hourlyRate.toFixed(2)} × {selectedDays} days 
                        </span>
                      </div>
                    ) : paymentOption !== 'Exam' && paymentOption !== 'Other' && (
                      <div className="breakdown-item highlight">
                        <span className="breakdown-label">
                          {paymentOption === '3ClassesPlus' ? 'Total Classes' : 'Classes'}
                        </span>
                        <span className="breakdown-calculation">
                          £{calculation.hourlyRate.toFixed(2)} × {calculation.lessonDuration}h × {calculation.numberOfClasses} {paymentOption === '3ClassesPlus' ? 'classes' : 'Classes'}
                        </span>
                      </div>
                    )}

                    {/* Original Amount */}
                    {paymentOption !== 'Exam' && (
                      <div className="breakdown-item">
                        <span className="breakdown-label">Subtotal</span>
                        <span className="breakdown-value">£{calculation.originalAmount.toFixed(2)}</span>
                      </div>
                    )}

                    {/* Discount */}
                    {calculation.discountAmount > 0 && (
                      <div className="breakdown-item discount">
                        <span className="breakdown-label">
                          {calculation.discountType} ({calculation.discountPercentage.toFixed(0)}%)
                        </span>
                        <span className="breakdown-value">-£{calculation.discountAmount.toFixed(2)}</span>
                      </div>
                    )}

                    {/* Divider */}
                    <div className="breakdown-divider" />
                    
                    {/* Total */}
                    <div className="breakdown-item total">
                      <span className="breakdown-label">Total Amount</span>
                      <span className="breakdown-value">£{calculation.originalAmount.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Final Amount Display */}
                  <div className="breakdown-final">
                    <span className="final-label">Final Amount</span>
                    <span className="final-amount">£{calculation.finalAmount.toFixed(2)}</span>
                  </div>

                  {/* Submit Button */}
                  <button
                    onClick={handleSubmit}
                    disabled={loading || !selectedStudent || calculation.finalAmount <= 0}
                    className="btn-submit-payment"
                  >
                    <CreditCard size={20} />
                    {loading ? 'Processing...' : `Collect £${calculation.finalAmount.toFixed(2)}`}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="inline-card">
              <div className="breakdown-placeholder">
                <Calculator size={48} />
                <h3>Payment Details</h3>
                <p>Select a student and payment option to see the breakdown</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentOptionsInline;